<script lang="ts">
	import { Strategy } from '$lib/interfaces/inner-interfaces';
	import type { InstallmentOption } from '$lib/server/httpRequests/learn-more.api';

	import type { TranslationKey } from '$lib/server/translations/translations.service';
	import { formatCurrency } from '$lib/services/formatter';
	import { translationsStore } from '$lib/store/translations.store';

	interface Props {
		strategy?: Strategy;
		totalAmount?: number;
		installment: InstallmentOption;
	}

	let { strategy = Strategy.SecuredPlan3, totalAmount = 0, installment }: Props = $props();

	const whys: TranslationKey[] = ['no_lean', 'no_interest', 'no_check', 'car_rewards'];
	const hows: TranslationKey[] = ['how_works_authorize', 'how_works_no_loan', 'how_works_held'];
	const table_headers: TranslationKey[] = ['monthly_payments', 'amount_paid', 'amount_held'];

	let bullets = $derived(
		(strategy === Strategy.NonSecuredPlan
			? ['bottom_3']
			: strategy === Strategy.SecuredPlan4
				? ['bottom_2', 'bottom_3']
				: ['bottom_1', 'bottom_2', 'bottom_3']) as TranslationKey[]
	);
</script>

<div class="learn-more-content bg-primary-background flex h-full flex-col">
	<div
		class="from-blue/70 to-green/70 relative flex min-h-fit flex-col items-start bg-gradient-to-r py-8 sm:flex-row sm:items-center"
	>
		<div class="w-full px-4 pt-4 sm:px-8 sm:pt-0">
			<p class="text-3xl text-white">{$translationsStore.monthly_payments_card}</p>
			{#if strategy !== Strategy.NonSecuredPlan}
				<p class="text-xl text-white">{$translationsStore.no_application_fee}</p>
			{/if}
		</div>
	</div>

	{#if strategy !== Strategy.NonSecuredPlan}
		<div class="bg-gray-bg px-4 py-7 sm:px-12">
			<p class="text-main px-2 pb-3 text-2xl sm:px-8">{$translationsStore.what_need}</p>
			<div class="text-surface rounded-md p-4 text-xl">
				<div class="flex items-center">
					<div class="h-2 w-2 min-w-[8px] rounded-full bg-black"></div>
					<span class="pl-4">{$translationsStore.full_credit}</span>
				</div>
				<div class="flex items-center">
					<div class="h-2 w-2 min-w-[8px] rounded-full bg-black"></div>
					<span class="pl-4">{$translationsStore.not_debits}</span>
				</div>
			</div>
		</div>
	{/if}

	<div class="xsm:px-6 grid flex-auto grid-cols-1 gap-4 px-2 py-7 sm:grid-cols-2 sm:px-12">
		<div>
			<p class="text-main px-2 text-xl">{$translationsStore.why_installments}</p>
			{#each whys as why}
				<div class="text-surface-dark flex items-center px-4 py-1 sm:px-8">
					<div class="h-[6px] w-[6px] min-w-[6px] rounded-full bg-black"></div>
					<span class="pl-4">{$translationsStore[why]}</span>
				</div>
			{/each}
		</div>
		{#if strategy === Strategy.SecuredPlan3}
			<div class="flex flex-col">
				<p class="text-main px-2 text-xl">{$translationsStore.example_plan}</p>
				<div
					class="xsm:flex-row xsm:text-center flex flex-auto flex-col items-center justify-center px-4 md:justify-start"
				>
					<div class="xsm:w-auto xsm:flex-col m-1 flex w-full items-center justify-between md:mx-3">
						<p class="xsm:w-24 mb-1 font-bold">{$translationsStore.purchase_price}</p>
						<div
							class="bg-gray-1 flex h-20 w-20 min-w-[80px] items-center justify-center rounded-full text-lg font-black text-white"
						>
							{formatCurrency(totalAmount)}
						</div>
					</div>

					<div class="xsm:w-auto xsm:flex-col m-1 flex w-full items-center justify-between md:mx-3">
						<p class="xsm:w-24 mb-1 font-bold">{$translationsStore.credit_required}</p>
						<div
							class="bg-gray-2 flex h-20 w-20 min-w-[80px] items-center justify-center rounded-full text-lg font-black text-white"
						>
							{formatCurrency(totalAmount)}
						</div>
					</div>

					<div class="xsm:w-auto xsm:flex-col m-1 flex w-full items-center justify-between md:mx-3">
						<p class="xsm:w-24 mb-1 font-bold">{$translationsStore.number_payments}</p>
						<div
							class="bg-gray-3 flex h-20 w-20 min-w-[80px] items-center justify-center rounded-full text-lg font-black text-white"
						>
							{installment.NumberOfInstallments}
						</div>
					</div>
				</div>
			</div>
		{/if}

		{#if strategy !== Strategy.NonSecuredPlan}
			<div class="px-2">
				<p class="text-main text-xl">{$translationsStore.how_works}</p>
				{#each hows as how}
					<div class="text-surface-dark flex items-center px-4 py-1 sm:px-8">
						<div class="h-[6px] min-h-[6px] w-[6px] min-w-[6px] rounded-full bg-black"></div>
						<span class="pl-4">{$translationsStore[how]}</span>
					</div>
				{/each}
			</div>
		{/if}

		{#if strategy === Strategy.SecuredPlan3}
			<div>
				<table class="w-full md:w-auto">
					<thead>
						<tr>
							{#each table_headers as header, idx}
								<th
									class:first-col={!idx}
									class="border-green xsm:max-w-[130px] max-w-[100px] border-b px-4 py-2 text-center leading-5"
								>
									{$translationsStore[header]}</th
								>
							{/each}
						</tr>
					</thead>
					<tbody>
						{#each installment.Installments as row, idx}
							<tr class:table-border={idx < installment.NumberOfInstallments - 1}>
								{#if !(installment.NumberOfInstallments > 4 && idx > 1 && idx < installment.NumberOfInstallments - 2)}
									<td class="xsm:text-base xsm:font-bold py-1 pl-2 text-sm font-semibold">
										{#if !(installment.NumberOfInstallments > 4 && idx > 2 && idx < installment.NumberOfInstallments - 1)}
											<p>
												{idx + 1}
												<span class="align-top text-xs"
													>{$translationsStore[
														!idx ? 'first' : idx === 1 ? 'second' : idx === 2 ? 'third' : 'fourth'
													]}</span
												>
												{$translationsStore.payment}
											</p>
										{:else}
											<div class="text-center">...</div>
										{/if}
									</td>
								{/if}

								{#if !(installment.NumberOfInstallments > 4 && idx > 1 && idx < installment.NumberOfInstallments - 2)}
									<td class="py-1 text-center">
										{#if !(installment.NumberOfInstallments > 4 && idx > 2 && idx < installment.NumberOfInstallments - 1)}
											<span> {formatCurrency(row.Amount)}</span>
										{:else}
											<div class="text-center">...</div>
										{/if}
									</td>
								{/if}

								{#if !(installment.NumberOfInstallments > 4 && idx > 1 && idx < installment.NumberOfInstallments - 2)}
									<td class="py-1 text-center">
										{#if !(installment.NumberOfInstallments > 4 && idx > 2 && idx < installment.NumberOfInstallments - 1)}
											<span>
												{formatCurrency(row.HeldAmount)}
											</span>
										{:else}
											<div class="text-center">...</div>
										{/if}
									</td>
								{/if}
							</tr>
						{/each}
					</tbody>
				</table>
			</div>
		{/if}
	</div>

	<div class="bg-gray-bg px-4 py-6 sm:px-10">
		{#each bullets as bullet}
			{#if $translationsStore[bullet]}
				<p class="text-surface py-2 text-xs">
					{$translationsStore[bullet]}
				</p>
			{/if}
		{/each}
	</div>
</div>

<style lang="postcss">
	.first-col {
		@apply pl-2 !text-left;
	}

	.table-border {
		@apply border-green border-b;
	}
</style>
